/**
 * Relatório do atendimento WhatsApp em PDF (mesmo padrão visual dos relatórios de projetos):
 * faixa verde com logo, período e filtros, indicadores com comparação ao período anterior,
 * tabelas por dia, ranking de atendentes e setores.
 */
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const VERDE = [21, 128, 61];
const ESCURO = [15, 23, 42];
const MEDIO = [71, 85, 105];
const BORDA = [226, 232, 240];
const FUNDO = [240, 253, 244];

export const fmtDur = (seg) => {
  if (seg == null) return '—';
  if (seg < 60) return `${Math.round(seg)} s`;
  const m = Math.round(seg / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h${String(m % 60).padStart(2, '0')}` : `${Math.floor(h / 24)}d ${h % 24}h`;
};
const dataBR = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString('pt-BR') : '—');
const num = (v) => (v == null ? '—' : Number(v).toLocaleString('pt-BR'));

export async function gerarRelatorioAtendimentoPDF({ dados, empresa, setorNome, geradoPor }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 12;
  const r = dados.resumo;
  const a = dados.anterior;

  doc.setFillColor(...VERDE); doc.rect(0, 0, W, 28, 'F');
  let x = M;
  if (empresa?.logo_base64) {
    try {
      const src = empresa.logo_base64.startsWith('data:') ? empresa.logo_base64 : `data:image/png;base64,${empresa.logo_base64}`;
      doc.addImage(src, 'PNG', M, 5, 18, 18); x = M + 22;
    } catch (_) { /* sem logo */ }
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
  doc.text('Relatório de atendimento WhatsApp', x, 13);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
  doc.text(empresa?.nome_empresa || 'AgroLink', x, 20);
  doc.text(`${dataBR(dados.periodo.inicio)} a ${dataBR(dados.periodo.fim)}`, W - M, 13, { align: 'right' });
  doc.text(`Setor: ${setorNome || 'todos'}`, W - M, 20, { align: 'right' });

  // indicadores
  const kpis = [
    ['Atendimentos', num(r.atendimentos), num(a.atendimentos)],
    ['Finalizados', num(r.finalizados), num(a.finalizados)],
    ['1ª resposta (média)', fmtDur(r.primeira_resposta_media), fmtDur(a.primeira_resposta_media)],
    ['Espera na fila', fmtDur(r.espera_media), fmtDur(a.espera_media)],
    ['Resolução (média)', fmtDur(r.resolucao_media), fmtDur(a.resolucao_media)],
    ['Avaliação', r.avaliacao_media != null ? `${r.avaliacao_media.toFixed(1).replace('.', ',')} / 5` : '—', a.avaliacao_media != null ? `${a.avaliacao_media.toFixed(1).replace('.', ',')} / 5` : '—'],
    ['Msgs recebidas', num(r.mensagens_recebidas), num(a.mensagens_recebidas)],
    ['Msgs enviadas', num(r.mensagens_enviadas), num(a.mensagens_enviadas)],
  ];
  if (dados.leads_disponivel) kpis.splice(2, 0, ['Leads novos', num(r.leads_novos), num(a.leads_novos)]);
  const cols = kpis.length;
  const gap = 3;
  const bw = (W - 2 * M - gap * (cols - 1)) / cols;
  let y = 34;
  kpis.forEach(([rot, val, ant], i) => {
    const bx = M + i * (bw + gap);
    doc.setFillColor(...FUNDO); doc.setDrawColor(...BORDA); doc.roundedRect(bx, y, bw, 21, 2, 2, 'FD');
    doc.setTextColor(...MEDIO); doc.setFontSize(7.5); doc.text(rot, bx + 3, y + 5.5);
    doc.setTextColor(...ESCURO); doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.text(String(val), bx + 3, y + 13);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(...MEDIO); doc.setFontSize(7); doc.text(`antes: ${ant}`, bx + 3, y + 18.5);
  });
  y += 27;

  const estilo = {
    theme: 'grid', styles: { fontSize: 8, cellPadding: 1.8, lineColor: BORDA, textColor: ESCURO },
    headStyles: { fillColor: VERDE, textColor: 255, fontStyle: 'bold' }, alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: M, right: M },
  };
  const titulo = (t) => { doc.setTextColor(...ESCURO); doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.text(t, M, y); doc.setFont('helvetica', 'normal'); y += 2; };

  titulo('Ranking de atendentes');
  autoTable(doc, {
    ...estilo, startY: y,
    head: [['#', 'Atendente', 'Atendimentos', 'Finalizados', '1ª resposta', 'Duração média', 'Avaliação', 'Mensagens enviadas']],
    body: dados.atendentes.length ? dados.atendentes.map(t => [t.posicao, t.nome, t.atendimentos, t.finalizados, fmtDur(t.primeira_resposta_media),
      fmtDur(t.duracao_media), t.avaliacao_media != null ? `${t.avaliacao_media.toFixed(1).replace('.', ',')} (${t.avaliacoes})` : '—', t.mensagens_enviadas])
      : [[{ content: 'Nenhum atendimento no período', colSpan: 8, styles: { halign: 'center', textColor: MEDIO } }]],
  });
  y = doc.lastAutoTable.finalY + 8;
  if (y > H - 40) { doc.addPage(); y = M + 4; }

  titulo('Setores');
  autoTable(doc, {
    ...estilo, startY: y,
    head: [['Setor', 'Atendimentos', 'Espera média', '1ª resposta', 'Resolução', 'Avaliação']],
    body: dados.setores.length ? dados.setores.map(s => [s.nome, s.atendimentos, fmtDur(s.espera_media), fmtDur(s.primeira_resposta_media),
      fmtDur(s.resolucao_media), s.avaliacao_media != null ? s.avaliacao_media.toFixed(1).replace('.', ',') : '—'])
      : [[{ content: 'Nenhum atendimento no período', colSpan: 6, styles: { halign: 'center', textColor: MEDIO } }]],
  });
  y = doc.lastAutoTable.finalY + 8;
  if (y > H - 40) { doc.addPage(); y = M + 4; }

  titulo('Por dia');
  autoTable(doc, {
    ...estilo, startY: y,
    head: [['Data', 'Atendimentos', 'Mensagens recebidas', 'Mensagens enviadas', '1ª resposta média']],
    body: dados.por_dia.map(d => [dataBR(d.data), d.atendimentos, d.recebidas, d.enviadas, fmtDur(d.primeira_resposta_media)]),
  });

  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFontSize(7.5); doc.setTextColor(...MEDIO);
    doc.text(`Gerado em ${new Date().toLocaleString('pt-BR')} por ${geradoPor || '—'}`, M, H - 6);
    doc.text(`Página ${i} de ${total}`, W - M, H - 6, { align: 'right' });
  }
  doc.save(`atendimento_${dados.periodo.inicio}_a_${dados.periodo.fim}.pdf`);
}
